
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Award, 
  Users, 
  TrendingUp, 
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Download,
  Settings,
  BarChart3,
  Shield,
  Database,
  Sliders
} from 'lucide-react';
import { AdminCertificateService } from '@/services/adminCertificateService';
import { toast } from '@/components/ui/sonner';
import CertificateStatsCards from './certificate/CertificateStatsCards';
import PendingCertificatesList from './certificate/PendingCertificatesList';
import CertificateAnalytics from './certificate/CertificateAnalytics';
import BulkCertificateActions from './certificate/BulkCertificateActions';
import CertificateAuditDashboard from './certificate/CertificateAuditDashboard';
import SecurityMonitoringPanel from './certificate/SecurityMonitoringPanel';
import CertificateToggleControls from './certificate/CertificateToggleControls';

const EnhancedCertificateManagement: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [pendingCertificates, setPendingCertificates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadCertificateData();
  }, []);

  const loadCertificateData = async () => {
    setLoading(true);
    try {
      const [statsData, pendingData] = await Promise.all([
        AdminCertificateService.getCertificateStats(),
        AdminCertificateService.getPendingCertificates()
      ]);
      
      setStats(statsData);
      setPendingCertificates(pendingData);
    } catch (error) {
      console.error('Error loading certificate data:', error);
      toast.error('Failed to load certificate data');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkGenerate = async () => {
    const eligibleCertificates = pendingCertificates.filter(cert => cert.canIssue);
    
    if (eligibleCertificates.length === 0) {
      toast.warning('No eligible certificates to generate');
      return;
    }

    setLoading(true);
    try {
      const result = await AdminCertificateService.bulkGenerateCertificates(eligibleCertificates);
      
      if (result.success > 0) {
        toast.success(`Generated ${result.success} certificates successfully`);
        await loadCertificateData();
      }
      
      if (result.failed > 0) {
        toast.warning(`${result.failed} certificates failed to generate`);
      }
    } catch (error) {
      console.error('Error in bulk generation:', error);
      toast.error('Failed to generate certificates');
    } finally {
      setLoading(false);
    }
  };

  if (loading && activeTab === 'overview') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Award className="h-6 w-6 text-primary" />
            NeoCertify Management
          </h2>
          <p className="text-muted-foreground">
            Comprehensive certificate management, security monitoring, and course controls
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={loadCertificateData} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={handleBulkGenerate} disabled={loading}>
            <Award className="h-4 w-4 mr-2" />
            Generate Pending
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {activeTab === 'overview' && <CertificateStatsCards stats={stats} />}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Pending ({pendingCertificates.length})
          </TabsTrigger>
          <TabsTrigger value="audit" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            Audit
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Security
          </TabsTrigger>
          <TabsTrigger value="controls" className="flex items-center gap-2">
            <Sliders className="h-4 w-4" />
            Controls
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="bulk-actions" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Bulk Actions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Recent Completions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Users who completed courses but haven't received certificates yet
                </p>
                <div className="space-y-2">
                  {pendingCertificates.slice(0, 5).map(cert => (
                    <div key={cert.userId} className="flex items-center justify-between p-2 bg-muted rounded">
                      <div>
                        <p className="font-medium">{cert.userName}</p>
                        <p className="text-sm text-muted-foreground">{cert.courseTitle}</p>
                      </div>
                      <Badge variant={cert.canIssue ? "default" : "secondary"}>
                        {cert.canIssue ? 'Ready' : 'Pending'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                  Generation Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span>Success Rate</span>
                    <span className="font-medium">
                      {stats ? Math.round((stats.totalIssued / (stats.totalIssued + stats.pendingGeneration)) * 100) : 0}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Avg. Score</span>
                    <span className="font-medium">{stats?.averageScore || 0}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Recent Activity</span>
                    <span className="font-medium">{stats?.recentlyIssued || 0} this week</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="pending">
          <PendingCertificatesList 
            certificates={pendingCertificates}
            onRefresh={loadCertificateData}
          />
        </TabsContent>

        <TabsContent value="audit">
          <CertificateAuditDashboard />
        </TabsContent>

        <TabsContent value="security">
          <SecurityMonitoringPanel />
        </TabsContent>

        <TabsContent value="controls">
          <CertificateToggleControls />
        </TabsContent>

        <TabsContent value="analytics">
          <CertificateAnalytics stats={stats} />
        </TabsContent>

        <TabsContent value="bulk-actions">
          <BulkCertificateActions 
            pendingCertificates={pendingCertificates}
            onRefresh={loadCertificateData}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EnhancedCertificateManagement;
